export type EmbedNetwork = 'instagram' | 'tiktok' | 'youtube' | 'x' | 'facebook' | 'pinterest';

// portrait: video vertical; landscape: 16:9; card: tarjeta de alto variable con scroll.
export type EmbedShape = 'portrait' | 'landscape' | 'card';

export interface PostEmbed {
  network: EmbedNetwork;
  label: string;
  src: string;
  shape: EmbedShape;
}

export const EMBED_NETWORK_LABELS: Record<EmbedNetwork, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  facebook: 'Facebook',
  pinterest: 'Pinterest',
};

export function safeExternalUrl(url: string | null | undefined): string | null {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'https:' || protocol === 'http:' ? trimmed : null;
  } catch {
    return null;
  }
}

function onDomain(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

function embed(network: EmbedNetwork, src: string, shape: EmbedShape): PostEmbed {
  return { network, label: EMBED_NETWORK_LABELS[network], src, shape };
}

function instagram(path: string[]): PostEmbed | null {
  // /p/{code}, /reel/{code}, /reels/{code}, /tv/{code}, y la variante con usuario delante.
  // /share/... lleva un token (no un shortcode) y /reels/audio/... es una pista de audio.
  if (path.includes('share')) return null;
  const at = path.findIndex(part => ['p', 'reel', 'reels', 'tv'].includes(part));
  const code = at >= 0 ? path[at + 1] : undefined;
  if (!code || code === 'audio') return null;
  return embed(
    'instagram',
    `https://www.instagram.com/p/${encodeURIComponent(code)}/embed/captioned/`,
    'portrait'
  );
}

function tiktok(path: string[]): PostEmbed | null {
  const id =
    path[0]?.startsWith('@') && ['video', 'photo'].includes(path[1])
      ? path[2]
      : path[0] === 'v'
        ? path[1]?.replace(/\.html$/, '')
        : path[0] === 'embed' && path[1] === 'v2'
          ? path[2]
          : undefined;
  if (!id || !/^\d+$/.test(id)) return null;
  return embed('tiktok', `https://www.tiktok.com/player/v1/${id}`, 'portrait');
}

function youtube(url: URL, host: string, path: string[]): PostEmbed | null {
  let id: string | null | undefined;
  let shape: EmbedShape = 'landscape';
  if (onDomain(host, 'youtu.be')) id = path[0];
  else if (path[0] === 'watch') id = url.searchParams.get('v');
  else if (path[0] === 'embed' || path[0] === 'live') id = path[1];
  else if (path[0] === 'shorts') {
    id = path[1];
    shape = 'portrait';
  }
  if (!id || id === 'videoseries' || !/^[\w-]{6,}$/.test(id)) return null;
  return embed('youtube', `https://www.youtube-nocookie.com/embed/${id}`, shape);
}

function x(path: string[]): PostEmbed | null {
  const at = path.indexOf('status');
  const id = at >= 1 ? path[at + 1] : undefined;
  if (!id || !/^\d+$/.test(id)) return null;
  return embed('x', `https://platform.twitter.com/embed/Tweet.html?id=${id}`, 'card');
}

function facebook(url: URL, path: string[]): PostEmbed | null {
  const isVideo =
    path.includes('videos') ||
    path[0] === 'reel' ||
    path[0] === 'watch' ||
    (path[0] === 'share' && path[1] === 'v');
  const isPost =
    path.includes('posts') ||
    path[0] === 'permalink.php' ||
    path[0] === 'story.php' ||
    path[0] === 'photo' ||
    path[0] === 'photo.php' ||
    // /{pagina}/photos/{album}/{id}: sin id detras es el listado de fotos, no una publicacion.
    (path.includes('photos') && path.length > path.indexOf('photos') + 1) ||
    (path[0] === 'share' && path[1] === 'p');
  if (path[0] === 'watch' && !url.searchParams.get('v')) return null;
  if (!isVideo && !isPost) return null;
  const plugin = isVideo ? 'video' : 'post';
  const href = encodeURIComponent(url.href);
  return embed(
    'facebook',
    `https://www.facebook.com/plugins/${plugin}.php?href=${href}&show_text=true&width=500`,
    'card'
  );
}

function pinterest(path: string[]): PostEmbed | null {
  const id = path[0] === 'pin' ? path[1] : undefined;
  if (!id || !/^\d+$/.test(id)) return null;
  return embed('pinterest', `https://assets.pinterest.com/ext/embed.html?id=${id}`, 'card');
}

/**
 * Traduce el enlace a una publicacion en la URL de su reproductor oficial incrustable.
 * Devuelve null para todo lo que no sea una publicacion concreta (perfiles, noticias,
 * enlaces cortos que solo el servidor de la red sabe resolver, URLs no http).
 */
export function resolvePostEmbed(rawUrl: string | null | undefined): PostEmbed | null {
  const safe = safeExternalUrl(rawUrl);
  if (!safe) return null;
  const url = new URL(safe);
  const host = url.hostname.toLowerCase();
  const path = url.pathname.split('/').filter(Boolean);

  if (onDomain(host, 'instagram.com')) return instagram(path);
  if (host === 'vm.tiktok.com' || host === 'vt.tiktok.com') return null;
  if (onDomain(host, 'tiktok.com')) return tiktok(path);
  if (onDomain(host, 'youtube.com') || onDomain(host, 'youtu.be')) return youtube(url, host, path);
  if (onDomain(host, 'x.com') || onDomain(host, 'twitter.com')) return x(path);
  if (onDomain(host, 'facebook.com')) return facebook(url, path);
  if (/(^|\.)pinterest\.(com|[a-z]{2}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(host)) {
    return pinterest(path);
  }
  return null;
}
